import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Play, Pause, RotateCcw, CheckCircle2, XCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Backgrounds themed to the story scene
const BG_MAP = {
  park: 'linear-gradient(180deg,#dcfce7 0%,#86efac 100%)',
  street: 'linear-gradient(180deg,#e0f2fe 0%,#bae6fd 100%)',
  room: 'linear-gradient(180deg,#fef3c7 0%,#fde68a 100%)',
  default: 'linear-gradient(180deg,#fafafa 0%,#e5e7eb 100%)',
};

const MOOD_ANIM = {
  neutral: '',
  warm: 'animate-[bounceSoft_1.6s_ease-in-out_infinite]',
  smile: 'animate-[bounceSoft_2s_ease-in-out_infinite]',
  wise: '',
  excited: 'animate-[wiggle_0.5s_ease-in-out_infinite]',
  careless: 'opacity-90',
  shrug: '',
  sheepish: 'opacity-95',
  curious: 'animate-[nod_1.4s_ease-in-out_infinite]',
  gentle: '',
  determined: 'animate-[bounceSoft_1s_ease-in-out_infinite]',
  doubtful: '',
  pleading: 'animate-[nod_1s_ease-in-out_infinite]',
  proud: 'animate-[bounceSoft_1.4s_ease-in-out_infinite]',
  teaching: '',
  protesting: 'animate-[wiggle_0.4s_ease-in-out_infinite]',
  teasing: 'animate-[wiggle_0.5s_ease-in-out_infinite]',
  shocked: 'animate-[wiggle_0.25s_ease-in-out_infinite]',
  beaming: 'animate-[bounceSoft_1.2s_ease-in-out_infinite]',
  annoyed: 'animate-[wiggle_0.5s_ease-in-out_infinite]',
  warming: '',
  helpful: 'animate-[bounceSoft_1.6s_ease-in-out_infinite]',
};

// Roughly 3 minutes total across all acts
const LINE_MS_MIN = 4000;
const LINE_MS_MAX = 12000;
const wordsToMs = (text) => Math.max(LINE_MS_MIN, Math.min(LINE_MS_MAX, (text || '').split(/\s+/).length * 350));

export default function StoryTheatrePage() {
  const { courseId, chapterIdx } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actIdx, setActIdx] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [phase, setPhase] = useState('story'); // 'story' | 'quiz' | 'done'
  const [quizAns, setQuizAns] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}/api/courses/${courseId}/story/${chapterIdx || 0}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setData(await res.json());
        setActIdx(0); setPhase('story'); setPlaying(true); setQuizAns({}); setSubmitted(false);
      } catch (e) {
        toast.error('Could not load story');
      } finally { setLoading(false); }
    })();
  }, [courseId, chapterIdx]);

  const acts = data?.story?.acts || [];
  const currentAct = acts[actIdx];

  // Auto-advance
  useEffect(() => {
    if (phase !== 'story' || !playing || !currentAct) return;
    const ms = wordsToMs(currentAct.text);
    timerRef.current = setTimeout(() => {
      if (actIdx + 1 < acts.length) setActIdx(actIdx + 1);
      else setPhase('quiz');
    }, ms);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [phase, playing, actIdx, currentAct, acts.length]);

  const skipToQuiz = () => { setPhase('quiz'); setPlaying(false); };
  const restart = () => { setActIdx(0); setPhase('story'); setPlaying(true); setQuizAns({}); setSubmitted(false); };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading story…</div>;
  if (!data) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-3">
      <div>Story not available for this chapter yet.</div>
      <Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
    </div>
  );

  const bg = BG_MAP[data.story.background] || BG_MAP.default;
  const cast = data.cast_avatars || {};
  const questions = data.story.questions || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="story-theatre-page">
      <style>{`
        @keyframes bounceSoft { 0%,100% { transform: translateY(0);} 50% { transform: translateY(-6px);} }
        @keyframes wiggle { 0%,100% { transform: rotate(-2deg);} 50% { transform: rotate(2deg);} }
        @keyframes nod { 0%,100% { transform: rotate(0);} 50% { transform: rotate(-4deg);} }
        @keyframes bubbleIn { from { opacity:0; transform: translateY(6px) scale(0.94);} to { opacity:1; transform: translateY(0) scale(1);} }
      `}</style>

      <header className="border-b bg-white/95 backdrop-blur">
        <div className="px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="story-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">{data.course_title}</h1>
            <p className="text-xs text-muted-foreground">Story · Chapter {parseInt(chapterIdx || 0) + 1}: {data.story.title}</p>
          </div>
          {phase === 'story' && (
            <>
              <Button size="sm" variant="outline" onClick={() => setPlaying(p => !p)} data-testid="story-toggle-play">
                {playing ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
                {playing ? 'Pause' : 'Play'}
              </Button>
              <Button size="sm" variant="outline" onClick={skipToQuiz} data-testid="story-skip">
                Skip to Quiz <Sparkles className="w-4 h-4 ml-1" />
              </Button>
            </>
          )}
        </div>
        {phase === 'story' && (
          <div className="h-1 bg-slate-200">
            <div className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-500 transition-all"
                 style={{ width: `${((actIdx + 1) / acts.length) * 100}%` }} />
          </div>
        )}
      </header>

      {phase === 'story' && (
        <div className="p-6">
          <div className="relative w-full max-w-5xl mx-auto rounded-3xl shadow-2xl overflow-hidden border border-slate-200"
               style={{ background: bg, height: '520px' }} data-testid="story-stage">
            <div className="absolute inset-0 flex items-end justify-around pb-4 gap-4 px-8">
              {(data.story.cast || []).map((castKey) => {
                const isActive = currentAct && currentAct.actor === castKey;
                return (
                  <div key={castKey} className={`relative flex flex-col items-center transition-all duration-500 ${isActive ? 'scale-110' : 'scale-90 opacity-60 grayscale-[30%]'}`}>
                    {isActive && (
                      <div className="mb-3 max-w-xs px-4 py-3 bg-white rounded-2xl shadow-lg border border-slate-200 text-sm text-slate-900"
                           style={{ animation: 'bubbleIn 0.35s ease-out both' }} data-testid={`bubble-${castKey}`}>
                        {currentAct.text}
                        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-0 h-0"
                             style={{ borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderTop: '12px solid white' }} />
                      </div>
                    )}
                    {cast[castKey] ? (
                      <img src={`${API}${cast[castKey]}`} alt={castKey}
                           className={`h-40 w-40 object-contain object-top rounded-2xl bg-white/50 border border-slate-200 ${isActive ? MOOD_ANIM[currentAct?.mood] || '' : ''}`}
                           data-testid={`actor-${castKey}`} />
                    ) : (
                      <div className="h-40 w-40 rounded-2xl bg-white/50 flex items-center justify-center text-5xl">🧑</div>
                    )}
                    <div className="mt-1 text-xs font-semibold text-slate-800 capitalize">{castKey}</div>
                  </div>
                );
              })}
            </div>
            {/* Scene label */}
            <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-white/80 text-xs font-semibold text-slate-700 shadow">
              {data.story.setting}
            </div>
          </div>
        </div>
      )}

      {phase === 'quiz' && (
        <QuizPanel
          questions={questions}
          answers={quizAns}
          setAnswers={setQuizAns}
          submitted={submitted}
          onSubmit={() => setSubmitted(true)}
          onRestart={restart}
        />
      )}
    </div>
  );
}

function QuizPanel({ questions, answers, setAnswers, submitted, onSubmit, onRestart }) {
  const score = useMemo(() => {
    if (!submitted) return 0;
    return questions.reduce((s, q, i) => s + (answers[i] === q.correct_index ? 1 : 0), 0);
  }, [answers, questions, submitted]);
  return (
    <div className="p-6 max-w-3xl mx-auto space-y-4" data-testid="story-quiz-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Now — 10 quick questions about the story</h2>
        <Button variant="outline" size="sm" onClick={onRestart} data-testid="story-restart">
          <RotateCcw className="w-4 h-4 mr-1" /> Replay story
        </Button>
      </div>
      {submitted && (
        <div className={`p-4 rounded-xl border-2 ${score >= 7 ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}
             data-testid="quiz-score-banner">
          <div className="text-lg font-bold">Score: {score} / {questions.length}</div>
          <div className="text-sm text-slate-600">
            {score === questions.length ? 'Perfect! You were paying attention.' :
             score >= 7 ? 'Great work — you caught most of the story.' :
             'Watch the story again — the answers are all in there.'}
          </div>
        </div>
      )}
      {questions.map((q, i) => (
        <div key={i} className="p-4 rounded-xl border bg-card" data-testid={`quiz-q-${i}`}>
          <div className="font-semibold mb-3">{i + 1}. {q.question}</div>
          <div className="space-y-2">
            {q.options.map((opt, oi) => {
              const chosen = answers[i] === oi;
              const isCorrect = submitted && oi === q.correct_index;
              const isWrong = submitted && chosen && oi !== q.correct_index;
              return (
                <button
                  key={oi}
                  onClick={() => !submitted && setAnswers({ ...answers, [i]: oi })}
                  disabled={submitted}
                  data-testid={`quiz-q-${i}-opt-${oi}`}
                  className={`w-full text-left px-3 py-2 rounded-lg border transition-all
                    ${isCorrect ? 'bg-emerald-100 border-emerald-400' : ''}
                    ${isWrong   ? 'bg-rose-100 border-rose-400' : ''}
                    ${!submitted && chosen ? 'bg-primary/10 border-primary/40' : ''}
                    ${!submitted ? 'hover:bg-primary/5' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full border flex items-center justify-center text-xs font-bold">
                      {String.fromCharCode(65 + oi)}
                    </span>
                    <span className="flex-1">{opt}</span>
                    {isCorrect && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                    {isWrong && <XCircle className="w-4 h-4 text-rose-600" />}
                  </div>
                </button>
              );
            })}
          </div>
          {submitted && (
            <div className="mt-3 text-xs text-slate-600 italic">💡 {q.explanation}</div>
          )}
        </div>
      ))}
      {!submitted ? (
        <Button className="w-full" size="lg" onClick={onSubmit}
                disabled={Object.keys(answers).length < questions.length}
                data-testid="quiz-submit-btn">
          Submit answers ({Object.keys(answers).length}/{questions.length})
        </Button>
      ) : (
        <Button className="w-full" size="lg" onClick={onRestart} data-testid="quiz-replay-btn">
          <RotateCcw className="w-4 h-4 mr-1" /> Watch again
        </Button>
      )}
    </div>
  );
}
